import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  MessageSquare,
  Wifi,
  WifiOff,
  Filter,
  Trash2,
  Search,
  Settings,
  Loader2,
  Terminal,
  AlertTriangle,
  Info,
  Play,
  Pause,
  Layers,
  Type,
  Eye,
  EyeOff,
  Plus,
  X,
  BarChart2,
  TrendingUp,
  Users,
  Activity,
  Clock,
  Trophy,
  Smile,
  Repeat,
  Zap,
} from "lucide-react";

// Kick's Public Pusher Key
const KICK_PUSHER_KEY = "32cbd69e4b950bf97679";
const KICK_PUSHER_CLUSTER = "us2";
const AYEZEE_CHATROOM_ID = "355800"; // Hardcoded ID for Ayezee

// Stop words to ignore in trending analysis
const STOP_WORDS = new Set([
  "the",
  "and",
  "is",
  "a",
  "to",
  "of",
  "in",
  "it",
  "for",
  "on",
  "with",
  "that",
  "this",
  "are",
  "be",
  "so",
  "if",
  "or",
  "at",
  "my",
  "me",
  "i",
  "you",
  "u",
]);

export default function App() {
  // --- Connection State ---
  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState("Initializing...");
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  // --- Chat Data State ---
  const [messages, setMessages] = useState([]);
  const [messageQueue, setMessageQueue] = useState([]);

  // --- View Settings ---
  const [isPaused, setIsPaused] = useState(false);
  const [groupSpam, setGroupSpam] = useState(true);
  const [compactMode, setCompactMode] = useState(false);
  const [hideEmojiOnly, setHideEmojiOnly] = useState(false);
  const [hideCommands, setHideCommands] = useState(false);
  const [hideDuplicates, setHideDuplicates] = useState(false);
  const [quickFilter, setQuickFilter] = useState("");
  const [fontSize, setFontSize] = useState(14);
  const [showTimestamps, setShowTimestamps] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showStats, setShowStats] = useState(false);

  // --- Statistics State ---
  const [msgRateHistory, setMsgRateHistory] = useState(new Array(60).fill(0)); // Last 60 seconds
  const [uniqueChatters, setUniqueChatters] = useState(new Set()); // Rolling window
  const [trendingWords, setTrendingWords] = useState([]);
  const [topChatters, setTopChatters] = useState([]);
  const [timeWindow, setTimeWindow] = useState(60); // Default 60 seconds
  const [trendNgram, setTrendNgram] = useState(1); // 1 = 1-word, 2 = 2-word phrase, etc.
  const MAX_RETENTION = 600; // Keep 10 minutes max for switching

  // Refs for tracking stats without re-renders inside high-frequency intervals
  const msgCountRef = useRef(0);
  const recentMessageTokensRef = useRef([]);
  const recentMessagesRef = useRef([]); // Stores { username, time } for Top Chatters
  const uniqueUsersRef = useRef(new Map()); // Map<Username, Timestamp>

  // --- Filters State ---
  const [filters, setFilters] = useState([
    { id: "default-1", type: "hide", target: "content", value: "!cash" },
    { id: "default-1", type: "hide", target: "content", value: "!join" },
    { id: "default-1", type: "hide", target: "content", value: "ayezeeCOIN" },
    { id: "default-1", type: "highlight", target: "user", value: "tezromania" },
  ]);
  const [newFilterType, setNewFilterType] = useState("hide");
  const [newFilterTarget, setNewFilterTarget] = useState("content");
  const [newFilterValue, setNewFilterValue] = useState("");

  // Refs
  const pusherRef = useRef(null);
  const chatContainerRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Load Pusher SDK & Set Document Title
  useEffect(() => {
    document.title = "ZeeChat"; // Update browser tab title

    if (window.Pusher) {
      setIsScriptLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://js.pusher.com/8.2.0/pusher.min.js";
    script.async = true;
    script.onload = () => {
      setIsScriptLoaded(true);
    };
    document.body.appendChild(script);
  }, []);

  // --- Statistics Loop (1s Interval) ---
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      // 1. Update Message Rate Graph
      const currentCount = msgCountRef.current;
      setMsgRateHistory((prev) => [...prev.slice(1), currentCount]);
      msgCountRef.current = 0; // Reset counter for next second

      // 2. Active Users Logic
      const now = Date.now();
      const activeUsers = new Set();
      const retentionCutoff = now - MAX_RETENTION * 1000;
      const displayCutoff = now - timeWindow * 1000;

      uniqueUsersRef.current.forEach((time, user) => {
        if (time < retentionCutoff) {
          uniqueUsersRef.current.delete(user);
        } else {
          if (time >= displayCutoff) activeUsers.add(user);
        }
      });
      setUniqueChatters(activeUsers);

      // 3. Update Trending & Top Chatters (Every 2s to save performance)
      if (new Date().getSeconds() % 2 === 0) {
        calculateStats(now, displayCutoff, retentionCutoff);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isConnected, timeWindow, trendNgram]);

  const calculateStats = (now, displayCutoff, retentionCutoff) => {
    // --- Trending Words (N-grams) ---
    const wordCounts = {};

    recentMessageTokensRef.current = recentMessageTokensRef.current.filter(
      (m) => m.time > retentionCutoff
    );
    const activeBatches = recentMessageTokensRef.current.filter(
      (m) => m.time >= displayCutoff
    );

    activeBatches.forEach((batch) => {
      const tokens = batch.tokens;
      if (tokens.length >= trendNgram) {
        for (let i = 0; i <= tokens.length - trendNgram; i++) {
          const phrase = tokens.slice(i, i + trendNgram).join(" ");
          wordCounts[phrase] = (wordCounts[phrase] || 0) + 1;
        }
      }
    });

    setTrendingWords(
      Object.entries(wordCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([word, count]) => ({ word, count }))
    );

    // --- Top Chatters ---
    const userCounts = {};
    recentMessagesRef.current = recentMessagesRef.current.filter(
      (m) => m.time > retentionCutoff
    );

    recentMessagesRef.current.forEach((msg) => {
      if (msg.time >= displayCutoff) {
        userCounts[msg.username] = (userCounts[msg.username] || 0) + 1;
      }
    });

    setTopChatters(
      Object.entries(userCounts)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .map(([username, count]) => ({ username, count }))
    );
  };

  // --- Auto Connect Logic ---
  useEffect(() => {
    if (!isScriptLoaded || isConnected) return;

    // Connect automatically
    connectToChat();
  }, [isScriptLoaded]);

  const connectToChat = () => {
    if (pusherRef.current) pusherRef.current.disconnect();

    setStatus("Connecting to Zee...");
    setMessages([]);
    setMessageQueue([]);
    setMsgRateHistory(new Array(60).fill(0));
    setTrendingWords([]);
    setTopChatters([]);

    msgCountRef.current = 0;
    recentMessageTokensRef.current = [];
    recentMessagesRef.current = [];
    uniqueUsersRef.current.clear();

    const pusher = new window.Pusher(KICK_PUSHER_KEY, {
      cluster: KICK_PUSHER_CLUSTER,
      forceTLS: true,
    });
    pusherRef.current = pusher;

    const channelName = `chatrooms.${AYEZEE_CHATROOM_ID}.v2`;
    const channel = pusher.subscribe(channelName);

    pusher.connection.bind("connected", () => {
      setIsConnected(true);
      setStatus("Connected");
    });
    pusher.connection.bind("disconnected", () => {
      setIsConnected(false);
      setStatus("Disconnected");
    });
    pusher.connection.bind("error", (err) => setStatus("Error"));

    const onMessage = (data) => {
      let parsed = data;
      if (typeof data === "string") {
        try {
          parsed = JSON.parse(data);
        } catch (e) {}
      }

      const content = parsed.content || "";
      const username = parsed.sender?.username || "Unknown";
      const now = Date.now();

      msgCountRef.current += 1;
      uniqueUsersRef.current.set(username, now);
      recentMessagesRef.current.push({ username, time: now });

      if (content) {
        const tokens = content
          .split(/[\s,!.?]+/)
          .filter((w) => w.length > 2)
          .map((w) => w.toLowerCase())
          .filter((w) => !STOP_WORDS.has(w));

        if (tokens.length > 0) {
          recentMessageTokensRef.current.push({ tokens, time: now });
        }
      }

      const newMsg = {
        id: parsed.id || Math.random().toString(36),
        content: content,
        sender: {
          username: username,
          color: parsed.sender?.identity?.color || "#53fc18",
        },
        timestamp: new Date().toLocaleTimeString([], { hour12: false }),
        count: 1,
      };

      if (isPaused) {
        setMessageQueue((prev) => [...prev, newMsg]);
      } else {
        processNewMessage(newMsg);
      }
    };

    channel.bind("App\\Events\\ChatMessageEvent", onMessage);
    channel.bind("ChatMessageEvent", onMessage);
  };

  const processNewMessage = (newMsg) => {
    setMessages((prev) => {
      if (groupSpam && prev.length > 0) {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg.content === newMsg.content) {
          const updatedLast = {
            ...lastMsg,
            count: lastMsg.count + 1,
            id: Math.random(),
          };
          return [...prev.slice(0, -1), updatedLast];
        }
      }
      const newHistory = [...prev, newMsg];
      if (newHistory.length > 500)
        return newHistory.slice(newHistory.length - 500);
      return newHistory;
    });
  };

  useEffect(() => {
    if (!isPaused && messageQueue.length > 0) {
      setMessages((prev) => {
        const combined = [...prev, ...messageQueue];
        return combined.slice(-500);
      });
      setMessageQueue([]);
    }
  }, [isPaused]);

  useEffect(() => {
    if (!isPaused && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isPaused]);

  // --- Filtering Logic ---
  const { visibleMessages, hiddenMatchCount } = useMemo(() => {
    const userLastMessage = new Map();
    const res = [];
    let hiddenCount = 0;

    messages.forEach((msg) => {
      let matchesSearch = true;
      if (quickFilter) {
        const lowerFilter = quickFilter.toLowerCase();
        const matchesContent = msg.content.toLowerCase().includes(lowerFilter);
        const matchesUser = msg.sender.username
          .toLowerCase()
          .includes(lowerFilter);
        if (!matchesContent && !matchesUser) {
          matchesSearch = false;
        }
      }

      if (!matchesSearch) return;

      let isHidden = false;

      if (hideEmojiOnly) {
        const withoutKickEmotes = msg.content.replace(
          /\[emote:\d+:[\w]+\]/g,
          ""
        );
        const trimmed = withoutKickEmotes.replace(/\s/g, "");
        if (trimmed.length === 0) isHidden = true;
        else {
          const withoutUnicode = trimmed.replace(
            /\p{Extended_Pictographic}/gu,
            ""
          );
          if (withoutUnicode.length === 0) isHidden = true;
        }
      }

      if (!isHidden && hideCommands) {
        if (msg.content.trim().startsWith("!")) isHidden = true;
      }

      if (!isHidden && hideDuplicates) {
        const lastContent = userLastMessage.get(msg.sender.username);
        if (lastContent === msg.content) isHidden = true;
        userLastMessage.set(msg.sender.username, msg.content);
      }

      if (!isHidden) {
        const hideFilters = filters.filter((f) => f.type === "hide");
        for (const filter of hideFilters) {
          const textToCheck =
            filter.target === "user" ? msg.sender.username : msg.content;
          if (textToCheck.toLowerCase().includes(filter.value.toLowerCase())) {
            isHidden = true;
            break;
          }
        }
      }

      if (isHidden) {
        hiddenCount++;
      } else {
        res.push(msg);
      }
    });

    return { visibleMessages: res, hiddenMatchCount: hiddenCount };
  }, [
    messages,
    filters,
    hideEmojiOnly,
    hideCommands,
    hideDuplicates,
    quickFilter,
  ]);

  // --- Emote Parser ---
  const parseMessageContent = (text) => {
    if (!text) return "";
    const regex = /\[emote:(\d+):([\w]+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex)
        parts.push(text.substring(lastIndex, match.index));
      const [_, emoteId, emoteName] = match;
      parts.push(
        <img
          key={`${emoteId}-${lastIndex}`}
          src={`https://files.kick.com/emotes/${emoteId}/fullsize`}
          alt={emoteName}
          className="inline-block align-middle h-6 w-auto mx-1 hover:scale-150 transition-transform relative z-10"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      );
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) parts.push(text.substring(lastIndex));
    return parts.length > 0 ? parts : text;
  };

  const addFilter = () => {
    if (!newFilterValue.trim()) return;
    setFilters([
      ...filters,
      {
        id: Math.random().toString(),
        type: newFilterType,
        target: newFilterTarget,
        value: newFilterValue,
      },
    ]);
    setNewFilterValue("");
  };

  const removeFilter = (id) => {
    setFilters(filters.filter((f) => f.id !== id));
  };

  const checkHighlight = (msg) => {
    const highlightFilters = filters.filter((f) => f.type === "highlight");
    for (const filter of highlightFilters) {
      const textToCheck =
        filter.target === "user" ? msg.sender.username : msg.content;
      if (textToCheck.toLowerCase().includes(filter.value.toLowerCase()))
        return true;
    }
    return false;
  };

  return (
    <div className="flex flex-col h-[100dvh] bg-[#0b0e0f] text-gray-200 font-sans overflow-hidden">
      {/* --- Top Control Bar --- */}
      <header className="h-16 border-b border-gray-800 bg-[#14171a] flex items-center justify-between px-3 md:px-4 shrink-0 shadow-sm z-20 sticky top-0 overflow-hidden">
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <div className="bg-[#53fc18] p-1.5 rounded text-black shadow-[0_0_10px_rgba(83,252,24,0.3)] shrink-0">
            <Zap size={20} strokeWidth={3} fill="black" />
          </div>

          <div className="min-w-0">
            <h1 className="text-base md:text-lg font-bold tracking-tight text-white leading-none truncate">
              Zee<span className="text-[#53fc18]">Chat</span>
            </h1>
            <div className="flex items-center gap-1.5 md:gap-2 mt-1">
              <span
                className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full shrink-0 ${
                  isConnected ? "bg-[#53fc18] animate-pulse" : "bg-red-500"
                }`}
              ></span>
              <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider truncate">
                {isConnected ? "LIVE" : status}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          {isConnected && (
            <>
              <button
                onClick={() => setIsPaused(!isPaused)}
                className={`flex items-center gap-1.5 md:gap-2 px-3 py-1.5 rounded-md text-xs md:text-sm font-bold transition-all ${
                  isPaused
                    ? "bg-orange-500 text-white animate-pulse"
                    : "bg-gray-800 hover:bg-gray-700 text-gray-300"
                }`}
              >
                {isPaused ? (
                  <>
                    <Pause size={14} className="shrink-0" />{" "}
                    <span className="hidden md:inline">PAUSED</span>
                    <span className="md:hidden">||</span>{" "}
                    <span className="text-[10px] md:text-xs opacity-80">
                      ({messageQueue.length})
                    </span>
                  </>
                ) : (
                  <>
                    <Play size={14} className="shrink-0" />{" "}
                    <span className="hidden md:inline">SCROLLING</span>
                  </>
                )}
              </button>
              <div className="h-6 w-px bg-gray-700 mx-1 md:mx-2"></div>
              <button
                onClick={() => {
                  setShowStats(!showStats);
                  setShowSettings(false);
                }}
                className={`p-2 rounded-md transition-colors flex items-center gap-2 text-xs font-bold ${
                  showStats
                    ? "bg-[#53fc18] text-black"
                    : "bg-gray-800 text-gray-400 hover:text-white"
                }`}
                aria-label="Statistics"
              >
                <BarChart2 size={18} />{" "}
                <span className="hidden md:inline">Stats</span>
              </button>
            </>
          )}

          <button
            onClick={() => {
              setShowSettings(!showSettings);
              setShowStats(false);
            }}
            className={`p-2 rounded-md transition-colors ${
              showSettings
                ? "bg-[#53fc18] text-black"
                : "bg-gray-800 text-gray-400 hover:text-white"
            }`}
            aria-label="Settings"
          >
            <Settings size={18} />
          </button>
        </div>
      </header>

      {/* --- Main Workspace --- */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Chat Wrapper */}
        <div className="flex-1 flex flex-col bg-[#0b0e0f] min-w-0">
          {/* Quick Filter Bar */}
          <div className="p-2 border-b border-gray-800 bg-[#0b0e0f] flex items-center gap-2 z-10">
            <div className="relative flex-1 max-w-md">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                size={14}
              />
              <input
                type="text"
                placeholder="Quick filter..."
                value={quickFilter}
                onChange={(e) => setQuickFilter(e.target.value)}
                className="w-full bg-[#14171a] border border-gray-700 rounded-md py-1.5 pl-9 pr-8 text-xs text-gray-200 focus:border-[#53fc18] focus:outline-none placeholder-gray-600"
              />
              {quickFilter && (
                <button
                  onClick={() => setQuickFilter("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            {quickFilter && (
              <div className="flex items-center gap-1 text-xs shrink-0">
                <span className="text-[#53fc18] font-bold">
                  {visibleMessages.length}
                </span>
                {hiddenMatchCount > 0 && (
                  <span className="text-gray-500 text-[10px]">
                    (+{hiddenMatchCount})
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Scrollable Chat Feed */}
          <div
            className="flex-1 overflow-y-auto relative scroll-smooth bg-[#0b0e0f]"
            ref={chatContainerRef}
          >
            <div className="min-h-full flex flex-col justify-end p-4">
              {visibleMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 opacity-30 select-none">
                  <MessageSquare size={64} className="mb-4" />
                  <p className="text-xl font-light">
                    {quickFilter
                      ? "No matches found"
                      : "Waiting for messages..."}
                  </p>
                  {quickFilter && hiddenMatchCount > 0 && (
                    <p className="text-sm text-yellow-500 mt-2">
                      {hiddenMatchCount} matches found but hidden by your
                      settings
                    </p>
                  )}
                  {!quickFilter && (
                    <p className="text-sm mt-2">
                      Connecting to <strong>Ayezee</strong>...
                    </p>
                  )}
                </div>
              ) : (
                visibleMessages.map((msg, idx) => {
                  const isHighlighted = checkHighlight(msg);
                  return (
                    <div
                      key={msg.id}
                      className={`
                            group relative flex items-start gap-2 hover:bg-white/5 transition-colors rounded px-2
                            ${compactMode ? "py-0.5" : "py-1.5"}
                            ${
                              isHighlighted
                                ? "bg-[#53fc18]/10 border-l-2 border-[#53fc18]"
                                : "border-l-2 border-transparent"
                            }
                          `}
                      style={{ fontSize: `${fontSize}px` }}
                    >
                      {showTimestamps && (
                        <span className="text-gray-600 font-mono text-[0.8em] mt-[0.2em] select-none shrink-0 w-[5ch] text-right">
                          {msg.timestamp.split(":").slice(0, 2).join(":")}
                        </span>
                      )}
                      <div className="flex-1 break-words leading-snug">
                        <span
                          className="font-bold cursor-pointer hover:underline mr-2"
                          style={{ color: msg.sender.color }}
                        >
                          {msg.sender.username}
                          {compactMode ? ":" : ""}
                        </span>
                        <span className="text-gray-300">
                          {parseMessageContent(msg.content)}
                        </span>
                      </div>
                      {msg.count > 1 && (
                        <div className="ml-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 shadow-sm animate-bounce">
                          x{msg.count}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {isPaused && (
              <div className="sticky bottom-4 left-1/2 -translate-x-1/2 bg-orange-600 text-white px-6 py-2 rounded-full shadow-lg font-bold text-sm z-10 flex items-center gap-2 pointer-events-none opacity-90">
                <Pause size={14} /> Chat Paused
              </div>
            )}
          </div>
        </div>

        {/* --- Stats Panel --- */}
        {showStats && (
          <aside className="w-80 bg-[#14171a] border-l border-gray-800 flex flex-col shadow-2xl z-30 animate-in slide-in-from-right duration-200 absolute right-0 top-0 bottom-0 md:relative">
            <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-[#191d21]">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Activity size={16} className="text-[#53fc18]" /> Live Analytics
              </h2>
              <button
                onClick={() => setShowStats(false)}
                className="text-gray-500 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Volume Graph */}
              <div className="bg-[#0b0e0f] rounded-lg border border-gray-800 p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">
                    Chat Volume
                  </span>
                  <span className="text-[#53fc18] font-mono text-xl font-bold">
                    {msgRateHistory[msgRateHistory.length - 1]}{" "}
                    <span className="text-xs text-gray-600">msg/sec</span>
                  </span>
                </div>
                {/* Dynamic SVG Sparkline */}
                <div className="h-24 w-full flex items-end gap-0.5 overflow-hidden">
                  {(() => {
                    // Determine Max Y Axis based on visible history, with a floor of 20
                    const maxVal = Math.max(...msgRateHistory, 20);

                    return msgRateHistory.map((val, i) => {
                      // Scale height relative to dynamic maxVal
                      const height = (val / maxVal) * 100;
                      return (
                        <div
                          key={i}
                          className="flex-1 bg-[#53fc18]/20 hover:bg-[#53fc18] transition-colors rounded-t-sm"
                          style={{ height: `${Math.max(4, height)}%` }}
                          title={`${val} msg/sec`}
                        />
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Active Users */}
              <div className="bg-[#0b0e0f] rounded-lg border border-gray-800 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-xs text-gray-500 font-bold uppercase tracking-wider">
                    <Users size={14} /> Active Users
                  </div>
                  <div className="flex bg-gray-800 rounded p-0.5">
                    {[60, 300, 600].map((seconds) => (
                      <button
                        key={seconds}
                        onClick={() => setTimeWindow(seconds)}
                        className={`text-[10px] px-2 py-0.5 rounded transition-colors ${
                          timeWindow === seconds
                            ? "bg-[#53fc18] text-black font-bold"
                            : "text-gray-400 hover:text-white"
                        }`}
                      >
                        {seconds / 60}m
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white text-3xl font-bold tracking-tight">
                    {uniqueChatters.size}
                  </span>
                  <div className="text-right">
                    <span className="text-xs text-gray-600 block">
                      Unique Chatters
                    </span>
                    <span className="text-[10px] text-gray-700 font-mono">
                      Window: {timeWindow / 60}m
                    </span>
                  </div>
                </div>
              </div>

              {/* Top Chatters */}
              <div className="bg-[#0b0e0f] rounded-lg border border-gray-800 p-4">
                <h3 className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Trophy size={12} className="text-yellow-500" /> Top Chatters{" "}
                  <span className="text-gray-600 text-[10px] font-normal lowercase">
                    ({timeWindow / 60}m)
                  </span>
                </h3>
                <div className="space-y-2">
                  {topChatters.length === 0 && (
                    <p className="text-xs text-gray-600 italic">
                      Waiting for data...
                    </p>
                  )}
                  {topChatters.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        setQuickFilter(item.username);
                        setShowStats(false);
                      }}
                      className="flex items-center justify-between group cursor-pointer hover:bg-white/5 rounded px-1 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`text-xs font-mono w-4 font-bold ${
                            idx === 0
                              ? "text-yellow-400"
                              : idx === 1
                              ? "text-gray-300"
                              : idx === 2
                              ? "text-orange-400"
                              : "text-gray-600"
                          }`}
                        >
                          #{idx + 1}
                        </span>
                        <span className="text-sm text-gray-300 font-medium group-hover:text-[#53fc18] transition-colors truncate max-w-[140px]">
                          {item.username}
                        </span>
                      </div>
                      <div className="bg-gray-800 px-2 py-0.5 rounded text-[10px] text-gray-400 min-w-[30px] text-center">
                        {item.count}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trending Words */}
              <div className="bg-[#0b0e0f] rounded-lg border border-gray-800 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs text-gray-500 font-bold uppercase tracking-wider flex items-center gap-2">
                    <TrendingUp size={12} /> Trending
                  </h3>
                  <div className="flex bg-gray-800 rounded p-0.5">
                    {[1, 2, 3].map((n) => (
                      <button
                        key={n}
                        onClick={() => setTrendNgram(n)}
                        className={`text-[10px] px-2 py-0.5 rounded transition-colors ${
                          trendNgram === n
                            ? "bg-[#53fc18] text-black font-bold"
                            : "text-gray-400 hover:text-white"
                        }`}
                        title={`${n}-word phrases`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  {trendingWords.length === 0 && (
                    <p className="text-xs text-gray-600 italic">
                      Waiting for data...
                    </p>
                  )}
                  {trendingWords.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        setQuickFilter(item.word);
                        setShowStats(false);
                      }}
                      className="flex items-center justify-between group cursor-pointer hover:bg-white/5 rounded px-1 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-gray-600 w-4">
                          #{idx + 1}
                        </span>
                        <span className="text-sm text-gray-300 font-medium group-hover:text-[#53fc18] transition-colors truncate max-w-[150px]">
                          {item.word.startsWith("[emote:")
                            ? parseMessageContent(item.word)
                            : item.word}
                        </span>
                      </div>
                      <div className="bg-gray-800 px-2 py-0.5 rounded text-[10px] text-gray-400 min-w-[30px] text-center">
                        {item.count}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        )}

        {/* --- Settings Sidebar --- */}
        {showSettings && (
          <aside className="w-80 bg-[#14171a] border-l border-gray-800 flex flex-col shadow-2xl z-30 animate-in slide-in-from-right duration-200 absolute right-0 top-0 bottom-0 md:relative">
            <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-[#191d21]">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Layers size={16} /> View Settings
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-500 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-8">
              {/* Appearance Section */}
              <section>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Type size={12} /> Appearance
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Compact Mode</span>
                    <button
                      onClick={() => setCompactMode(!compactMode)}
                      className={`w-10 h-5 rounded-full transition-colors relative ${
                        compactMode ? "bg-[#53fc18]" : "bg-gray-700"
                      }`}
                    >
                      <div
                        className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${
                          compactMode ? "left-6" : "left-1"
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">
                      Group Spam (Combo)
                    </span>
                    <button
                      onClick={() => setGroupSpam(!groupSpam)}
                      className={`w-10 h-5 rounded-full transition-colors relative ${
                        groupSpam ? "bg-[#53fc18]" : "bg-gray-700"
                      }`}
                    >
                      <div
                        className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${
                          groupSpam ? "left-6" : "left-1"
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">
                      Hide Emoji-Only Messages
                    </span>
                    <button
                      onClick={() => setHideEmojiOnly(!hideEmojiOnly)}
                      className={`w-10 h-5 rounded-full transition-colors relative ${
                        hideEmojiOnly ? "bg-[#53fc18]" : "bg-gray-700"
                      }`}
                    >
                      <div
                        className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${
                          hideEmojiOnly ? "left-6" : "left-1"
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">
                      Hide Command Messages (!)
                    </span>
                    <button
                      onClick={() => setHideCommands(!hideCommands)}
                      className={`w-10 h-5 rounded-full transition-colors relative ${
                        hideCommands ? "bg-[#53fc18]" : "bg-gray-700"
                      }`}
                    >
                      <div
                        className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${
                          hideCommands ? "left-6" : "left-1"
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">
                      Hide Duplicates (Same User)
                    </span>
                    <button
                      onClick={() => setHideDuplicates(!hideDuplicates)}
                      className={`w-10 h-5 rounded-full transition-colors relative ${
                        hideDuplicates ? "bg-[#53fc18]" : "bg-gray-700"
                      }`}
                    >
                      <div
                        className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${
                          hideDuplicates ? "left-6" : "left-1"
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">
                      Show Timestamps
                    </span>
                    <button
                      onClick={() => setShowTimestamps(!showTimestamps)}
                      className={`w-10 h-5 rounded-full transition-colors relative ${
                        showTimestamps ? "bg-[#53fc18]" : "bg-gray-700"
                      }`}
                    >
                      <div
                        className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${
                          showTimestamps ? "left-6" : "left-1"
                        }`}
                      />
                    </button>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm text-gray-300 mb-1">
                      <span>Font Size</span>
                      <span>{fontSize}px</span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="24"
                      value={fontSize}
                      onChange={(e) => setFontSize(parseInt(e.target.value))}
                      className="w-full accent-[#53fc18] h-1.5 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              </section>

              {/* Filters Section */}
              <section>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Filter size={12} /> Filters
                </h3>

                {/* Filter Creator */}
                <div className="bg-[#0b0e0f] p-3 rounded-lg border border-gray-700 mb-4 space-y-2">
                  <div className="flex gap-2 text-xs">
                    <button
                      onClick={() => setNewFilterType("hide")}
                      className={`flex-1 py-1.5 rounded border ${
                        newFilterType === "hide"
                          ? "bg-red-500/20 text-red-400 border-red-500/50"
                          : "border-transparent text-gray-500 hover:bg-gray-800"
                      }`}
                    >
                      <EyeOff size={12} className="inline mr-1" /> Hide
                    </button>
                    <button
                      onClick={() => setNewFilterType("highlight")}
                      className={`flex-1 py-1.5 rounded border ${
                        newFilterType === "highlight"
                          ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/50"
                          : "border-transparent text-gray-500 hover:bg-gray-800"
                      }`}
                    >
                      <Eye size={12} className="inline mr-1" /> Highlight
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={newFilterTarget}
                      onChange={(e) => setNewFilterTarget(e.target.value)}
                      className="bg-gray-800 text-xs rounded px-2 outline-none border border-gray-700"
                    >
                      <option value="content">Word</option>
                      <option value="user">User</option>
                    </select>
                    <input
                      className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs outline-none focus:border-[#53fc18]"
                      placeholder="Value..."
                      value={newFilterValue}
                      onChange={(e) => setNewFilterValue(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addFilter()}
                    />
                    <button
                      onClick={addFilter}
                      className="bg-[#53fc18] text-black rounded p-1 hover:bg-[#46d615]"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>

                {/* Active Filters List */}
                <div className="space-y-2">
                  {filters.length === 0 && (
                    <p className="text-xs text-gray-600 italic text-center">
                      No active filters
                    </p>
                  )}
                  {filters.map((f) => (
                    <div
                      key={f.id}
                      className="flex items-center justify-between bg-[#191d21] p-2 rounded border border-gray-800 text-xs group"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            f.type === "hide" ? "bg-red-500" : "bg-yellow-500"
                          }`}
                        />
                        <span className="text-gray-400 uppercase text-[10px]">
                          {f.target}:
                        </span>
                        <span className="text-gray-200 font-medium truncate max-w-[120px]">
                          {f.value}
                        </span>
                      </div>
                      <button
                        onClick={() => removeFilter(f.id)}
                        className="text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
